/**
 * CausalNarrativeDisplay — renders the LLM-generated "why this works" prose
 * with inline PDO concept annotations.
 *
 * Annotated phrases appear bold with a subtle dotted underline and a (?)
 * superscript. Hovering (?) opens a small popup that describes the PDO concept
 * and the causal relationship our system validated. Raw technical concept pills
 * are preserved in a collapsible "Technical view" for power users.
 */

import { useRef, useState } from 'react';
import { clsx } from 'clsx';

// ---------------------------------------------------------------------------
// Types (mirrors backend NarrativeAnnotation)
// ---------------------------------------------------------------------------

export interface NarrativeAnnotation {
  phrase: string;
  concept_id: string;
  concept_name: string;
  concept_class: string; // ATTRIBUTE | NEED | VALUE | TRUST | FACTOR
  edge_type: string; // DRIVES | RESOLVES_CONCERN | INHIBITS | ACTIVATES
  weight: number;
  popup_title: string;
  popup_body: string;
}

export interface CausalNarrativeData {
  prose: string;
  annotations: NarrativeAnnotation[];
  generated_at?: string | null;
  model?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EDGE_COLOR: Record<string, string> = {
  DRIVES: 'text-amber-700',
  RESOLVES_CONCERN: 'text-teal-700',
  INHIBITS: 'text-rose-700',
  ACTIVATES: 'text-indigo-700',
};

const EDGE_LABEL: Record<string, string> = {
  DRIVES: 'Drives',
  RESOLVES_CONCERN: 'Resolves concern',
  INHIBITS: 'Inhibits',
  ACTIVATES: 'Activates',
};

const CLASS_BADGE: Record<string, string> = {
  ATTRIBUTE: 'bg-sky-50 text-sky-700',
  NEED: 'bg-violet-50 text-violet-700',
  VALUE: 'bg-amber-50 text-amber-700',
  TRUST: 'bg-teal-50 text-teal-700',
  FACTOR: 'bg-slate-100 text-slate-600',
};

/**
 * Split prose into segments: plain text and annotated spans.
 * Processes longest phrase first to avoid nested / overlapping matches.
 */
function tokenize(
  prose: string,
  annotations: NarrativeAnnotation[],
): Array<{ type: 'text'; content: string } | { type: 'annotation'; annotation: NarrativeAnnotation }> {
  if (!annotations.length) return [{ type: 'text', content: prose }];

  // Sort longest → shortest so inner substrings don't consume outer matches
  const sorted = [...annotations].sort((a, b) => b.phrase.length - a.phrase.length);

  type Token =
    | { type: 'text'; content: string }
    | { type: 'annotation'; annotation: NarrativeAnnotation };

  let segments: Token[] = [{ type: 'text', content: prose }];

  for (const ann of sorted) {
    const next: Token[] = [];
    for (const seg of segments) {
      if (seg.type !== 'text') {
        next.push(seg);
        continue;
      }
      const idx = seg.content.indexOf(ann.phrase);
      if (idx === -1) {
        next.push(seg);
        continue;
      }
      if (idx > 0) next.push({ type: 'text', content: seg.content.slice(0, idx) });
      next.push({ type: 'annotation', annotation: ann });
      const rest = seg.content.slice(idx + ann.phrase.length);
      if (rest) next.push({ type: 'text', content: rest });
    }
    segments = next;
  }

  return segments;
}

// ---------------------------------------------------------------------------
// Popup
// ---------------------------------------------------------------------------

function PDOPopup({ annotation, onClose }: { annotation: NarrativeAnnotation; onClose: () => void }) {
  const edgeColor = EDGE_COLOR[annotation.edge_type] ?? 'text-slate-600';
  const edgeLabel = EDGE_LABEL[annotation.edge_type] ?? annotation.edge_type;
  const classBadge = CLASS_BADGE[annotation.concept_class] ?? 'bg-slate-100 text-slate-600';

  return (
    <div
      className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-lg shadow-lg border border-slate-200 bg-white text-left"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Arrow */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-200" />

      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[11px] font-bold text-slate-800 leading-tight">
            {annotation.popup_title}
          </span>
          <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide', classBadge)}>
            {annotation.concept_class}
          </span>
        </div>

        <p className="text-[11px] text-slate-600 leading-relaxed">{annotation.popup_body}</p>

        <div className="flex items-center gap-1.5 pt-1 border-t border-slate-100">
          <span className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">
            Causal edge:
          </span>
          <span className={clsx('text-[10px] font-semibold', edgeColor)}>{edgeLabel}</span>
          <span className="text-[9px] text-slate-400 ml-auto">
            {(annotation.weight * 100).toFixed(0)}% weight
          </span>
        </div>
      </div>

      <button
        type="button"
        className="absolute top-1.5 right-1.5 text-slate-300 hover:text-slate-500 text-[10px] px-1"
        onClick={onClose}
      >
        ✕
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Annotated phrase inline element
// ---------------------------------------------------------------------------

function AnnotatedPhrase({ annotation }: { annotation: NarrativeAnnotation }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  return (
    <span className="relative inline">
      {/* The annotated phrase itself */}
      <span
        className="font-semibold text-slate-800 border-b border-dotted border-slate-400 cursor-default"
        style={{ borderBottomStyle: 'dotted' }}
      >
        {annotation.phrase}
      </span>
      {/* (?) trigger */}
      <button
        ref={ref as any}
        type="button"
        className={clsx(
          'ml-0.5 text-[9px] text-slate-400 hover:text-teal-600 font-semibold align-super leading-none transition-colors',
          open && 'text-teal-600',
        )}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={annotation.popup_title}
      >
        (?)
      </button>
      {/* Popup */}
      {open && (
        <PDOPopup annotation={annotation} onClose={() => setOpen(false)} />
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CausalNarrativeDisplay({ narrative }: { narrative: CausalNarrativeData }) {
  const tokens = tokenize(narrative.prose, narrative.annotations);

  return (
    <div className="text-[12px] text-slate-600 leading-relaxed">
      {tokens.map((tok, i) =>
        tok.type === 'text' ? (
          <span key={i}>{tok.content}</span>
        ) : (
          <AnnotatedPhrase key={i} annotation={tok.annotation} />
        ),
      )}
    </div>
  );
}
